import { useRef, useState } from "react";
import { FileUp, NotebookPen, Download, Trash2, FileText, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { TextField, AutoTextarea } from "@/components/tools/fields";
import { Panel, ErrorText, Confirm, PseudoNotice, SelectField, fmtDate } from "@/components/rc/shared";

const AREAS = ["", "Leadership", "Identification", "Provision", "EHCP reviews", "Parent partnership", "Staffing and CPD", "Outcomes", "Governance", "Other"];
const ACCEPT = ".pdf,.doc,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.txt,.csv";

const NoteForm = ({ onDone }) => {
  const [f, setF] = useState({ title: "", area: "", body: "", ok: false });
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.ok) return setError("Please confirm the note contains no pupil names or identifiable details.");
    try {
      await api.post("/rc/evidence/note", { title: f.title, area: f.area, body: f.body, confirm_pseudonymised: true });
      setF({ title: "", area: "", body: "", ok: false });
      setError("");
      toast.success("Note saved to your workspace");
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
    return undefined;
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="evidence-note-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="ev-n-title" label="Title" value={f.title} onChange={set("title")} testid="evidence-note-title" />
        <SelectField id="ev-n-area" label="Area" value={f.area} onChange={set("area")} options={AREAS} testid="evidence-note-area" />
      </div>
      <AutoTextarea id="ev-n-body" label="Type your evidence or information" rows={5} value={f.body} onChange={set("body")} testid="evidence-note-body" />
      <Confirm id="ev-n-ok" checked={f.ok} onChange={set("ok")} testid="evidence-note-confirm">I confirm this contains no pupil names or identifiable details (pupil reference codes only).</Confirm>
      <ErrorText error={error} testid="evidence-note-error" />
      <button type="submit" className="btn btn-brand" data-testid="evidence-note-submit"><NotebookPen size={16} aria-hidden /> Save note</button>
    </form>
  );
};

const UploadForm = ({ onDone }) => {
  const input = useRef(null);
  const [f, setF] = useState({ title: "", area: "", file: null, ok: false });
  const [error, setError] = useState("");
  const [pct, setPct] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.file) return setError("Please choose a file.");
    if (f.file.size > 10 * 1024 * 1024) return setError("Files must be under 10 MB.");
    if (!f.ok) return setError("Please confirm the document contains no pupil names or identifiable details.");
    const form = new FormData();
    form.append("file", f.file);
    form.append("title", f.title || f.file.name);
    form.append("area", f.area);
    form.append("confirm_pseudonymised", "true");
    setError("");
    setPct(0);
    try {
      await api.post("/rc/evidence/upload", form, { onUploadProgress: (p) => setPct(Math.round((p.loaded / (p.total || 1)) * 100)) });
      setF({ title: "", area: "", file: null, ok: false });
      if (input.current) input.current.value = "";
      toast.success("Document uploaded and encrypted");
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPct(null);
    }
    return undefined;
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="evidence-upload-form">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ev-u-file" className="field-label">Document (PDF, Word, Excel, PowerPoint, image, text or CSV, up to 10 MB)</label>
        <input ref={input} id="ev-u-file" type="file" accept={ACCEPT} onChange={(e) => set("file")(e.target.files?.[0] || null)} className="field-input" data-testid="evidence-upload-file" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="ev-u-title" label="Title" value={f.title} onChange={set("title")} testid="evidence-upload-title" />
        <SelectField id="ev-u-area" label="Area" value={f.area} onChange={set("area")} options={AREAS} testid="evidence-upload-area" />
      </div>
      <Confirm id="ev-u-ok" checked={f.ok} onChange={set("ok")} testid="evidence-upload-confirm">I confirm this document has been pseudonymised and contains no pupil names, photos or identifiable details.</Confirm>
      <ErrorText error={error} testid="evidence-upload-error" />
      {pct !== null && <p role="status" className="font-bold" data-testid="evidence-upload-progress">Uploading… {pct}%</p>}
      <button type="submit" disabled={pct !== null} className="btn btn-brand" data-testid="evidence-upload-submit"><FileUp size={16} aria-hidden /> Upload document</button>
    </form>
  );
};

const download = async (ev) => {
  try {
    const res = await api.get(`/rc/evidence/${ev.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = ev.original_filename || "document";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    toast.error(errorText(err));
  }
};

const Item = ({ ev, canDelete, onDelete }) => (
  <li className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border-2 border-line bg-paper p-4" data-testid={`evidence-item-${ev.id}`}>
    <div className="min-w-0 flex-1">
      <p className="flex items-center gap-2 font-bold">{ev.kind === "file" ? <FileText size={16} aria-hidden /> : <StickyNote size={16} aria-hidden />}{ev.title}</p>
      <p className="text-sm text-ink-soft">{ev.area || "General"} · {ev.created_by} · {fmtDate(ev.created_at)}{ev.kind === "file" ? ` · ${ev.original_filename} (${Math.max(1, Math.round(ev.size / 1024))} KB)` : ""}</p>
      {ev.body && <p className="mt-2 whitespace-pre-wrap text-sm">{ev.body}</p>}
    </div>
    <div className="flex gap-2">
      {ev.kind === "file" && <button type="button" onClick={() => download(ev)} className="btn btn-ghost btn-sm" data-testid={`evidence-download-${ev.id}`}><Download size={14} aria-hidden /> Download</button>}
      {canDelete(ev) && <button type="button" onClick={() => onDelete(ev)} className="btn btn-ghost btn-sm" aria-label={`Remove ${ev.title}`} data-testid={`evidence-delete-${ev.id}`}><Trash2 size={14} aria-hidden /></button>}
    </div>
  </li>
);

export const Evidence = ({ items, canWrite, canDelete, reload }) => {
  const [mode, setMode] = useState("note");
  const remove = async (ev) => {
    if (!window.confirm(`Remove "${ev.title}"?`)) return;
    try {
      await api.delete(`/rc/evidence/${ev.id}`);
      reload();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <Panel id="ws-evidence" eyebrow="Encrypted per school" title="Evidence and information">
      {canWrite && (
        <div className="mb-8 space-y-5">
          <PseudoNotice />
          <div role="tablist" aria-label="Add evidence" className="flex flex-wrap gap-2">
            <button type="button" role="tab" aria-selected={mode === "note"} onClick={() => setMode("note")} className={`chip ${mode === "note" ? "bg-brand text-brand-fg" : ""}`} data-testid="evidence-mode-note"><NotebookPen size={14} aria-hidden /> Type it in</button>
            <button type="button" role="tab" aria-selected={mode === "file"} onClick={() => setMode("file")} className={`chip ${mode === "file" ? "bg-brand text-brand-fg" : ""}`} data-testid="evidence-mode-file"><FileUp size={14} aria-hidden /> Upload a document</button>
          </div>
          {mode === "note" ? <NoteForm onDone={reload} /> : <UploadForm onDone={reload} />}
        </div>
      )}
      {items.length ? (
        <ul className="space-y-3" data-testid="evidence-list">{items.map((ev) => <Item key={ev.id} ev={ev} canDelete={canDelete} onDelete={remove} />)}</ul>
      ) : <p className="rounded-2xl border-2 border-dashed border-line p-6 text-ink-soft" data-testid="evidence-empty">No evidence added yet.</p>}
    </Panel>
  );
};
